import styled from "styled-components";
import type { MonitorEvent } from "../../../models/AmlMonitoringEvents";
import { theme } from "../../../theme";
import { useTranslation } from "react-i18next";
import Body from "../../atoms/typography/Body";
import { getMonitorEventTitles } from "../../../utils/amlHelpers";
import { HorizontalSpacer } from "../../atoms/Spacers";
import BodySmall from "../../atoms/typography/BodySmall";
import Divider from "../../atoms/Divider";
import AmlMonitorException from "./AmlMonitorException";
import TextField from "../../atoms/inputs/TextField";
import { useState } from "react";
import ActionButton from "../../atoms/buttons/ActionButton";
import { CommentIcon } from "../../atoms/icons/CommentIcon";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import Small from "../../atoms/typography/Small";
import dayjs from "dayjs";
import Alert from "../Alert";
import { useCreateMonitorEventComment } from "../../../graphql/hooks/aml/useCreateMonitorEventComments";
import { useMarkMonitorEventAsHandled } from "../../../graphql/hooks/aml/useMarkMonitorEventAsHandled";
import CircleIcon from "../../atoms/CircleIcon";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";

interface Props {
  clientIdentifier: string;
  event: MonitorEvent;
  refetch?: () => void;
}

const FlexedCenteredDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClientEventDetailedActionComponent: React.FC<Props> = ({
  clientIdentifier,
  event,
  refetch,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onCompletedLeaveComment = (result: any) => {
    if (result?.aml?.createMonitorEventComment.success) {
      setComment("");
      setSuccessMessage(t("aml.commentAddedSuccessfully"));
      setErrorMessage("");
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } else {
      setErrorMessage(t("aml.commentCouldNotBeAdded"));
      setSuccessMessage("");
    }
  };
  const onErrorLeaveComment = (e?: any) => {
    setErrorMessage(e?.toString());
    setSuccessMessage("");
  };
  const {
    createMonitorEventComment,
    error: commentError,
    loading: commentLoading,
  } = useCreateMonitorEventComment({
    onCompleted: onCompletedLeaveComment,
    onError: onErrorLeaveComment,
  });

  const onClickLeaveComment = async () => {
    try {
      await createMonitorEventComment({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            comment: comment,
            monitoringEventId: event.id,
          },
        },
      });
    } catch (error) {
      setErrorMessage(error?.toString() ?? t("aml.commentCouldNotBeAdded"));
    }
  };

  const onCompletedMarkAsHandled = (result?: any) => {
    if (result?.aml?.markMonitorEventAsHandled?.success) {
      setSuccessMessage(t("aml.theEventHasBeenMarkedAsHandled"));
      setErrorMessage("");
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } else {
      setErrorMessage(t("aml.theEventCouldNotBeMarkedAsHandled"));
      setSuccessMessage("");
    }
  };
  const onErrorMarkAsHandled = (e?: any) => {
    setErrorMessage(e?.toString() || t("errors.somethingWentWrong"));
    setSuccessMessage("");
  };

  const { markMonitorEventAsHandled, error, loading } =
    useMarkMonitorEventAsHandled({
      onCompleted: onCompletedMarkAsHandled,
      onError: onErrorMarkAsHandled,
    });

  const onClickMarkAsHandled = async () => {
    try {
      await markMonitorEventAsHandled({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            comment: comment,
            monitoringEventId: event.id,
          },
        },
      });
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error?.toString() || t("aml.theEventCouldNotBeMarkedAsHandled"),
      );
    }
  };

  return (
    <div>
      <FlexedCenteredDiv>
        <CircleIcon type={event.handled ? "success" : "error"} />
        <Body fontweight="600">
          {t(getMonitorEventTitles(event.eventType))}
        </Body>
      </FlexedCenteredDiv>
      <HorizontalSpacer spacing={1} />
      {event.descriptionLines?.map((line, index) => (
        <BodySmall key={index} color={theme.colors.textSecondary}>
          {line}
        </BodySmall>
      ))}
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      {event.parameters.map((param) => (
        <div
          key={param.id}
          style={{
            padding: 16,
            borderRadius: 12,
            border: `1px solid ${theme.colors.grey05}`,
            margin: 8,
          }}
        >
          <BodySmall fontweight={600}>{param.title}</BodySmall>

          {event.parameterExclusionOptions && (
            <AmlMonitorException
              parameter={param}
              parameterExclusionOptions={event.parameterExclusionOptions}
              monitoringEventId={event.id}
              clientIdentifier={clientIdentifier}
              refetch={refetch}
            />
          )}
        </div>
      ))}

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />

      <TextField
        value={comment}
        onChange={setComment}
        placeholder={t("aml.comment")}
        fullWidth
      />
      <HorizontalSpacer spacing={1} />
      <div style={{ display: "flex", gap: 32, justifyContent: "flex-end" }}>
        <ActionButton
          color={theme.colors.primary}
          onClick={onClickLeaveComment}
          disabled={comment.length < 1 || successMessage.length > 0}
          title={t("aml.leaveComment")}
          loading={commentLoading}
          icon={<CommentIcon color={theme.colors.white} />}
          small
        />
        <ActionButton
          color={theme.colors.primary}
          onClick={onClickMarkAsHandled}
          disabled={comment.length < 1 || successMessage.length > 0}
          title={t("aml.saveAndMarkAsHandled")}
          loading={loading}
          icon={<CheckmarkIconSmall color={theme.colors.white} />}
          small
        />
      </div>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />

      <Body fontweight="600">{t("aml.commentsAndHistory")}</Body>
      <HorizontalSpacer spacing={1} />
      {event.comments?.items?.map((c) => (
        <div key={c.id}>
          <Small>
            {dayjs(c.createdAt).format("YYYY-MM-DD HH:mm")} -{" "}
            {c.createdByUser?.firstName} {c.createdByUser?.lastName}
          </Small>
          <BodySmall>{c.content}</BodySmall>
          <HorizontalSpacer spacing={1} />
        </div>
      ))}

      {successMessage && (
        <Alert
          type="success"
          title={successMessage}
          message={
            <OutlinedButton
              title={t("aml.refresh")}
              onClick={() => {
                refetch && refetch();
              }}
              color={theme.colors.primary}
            />
          }
        />
      )}
      {(errorMessage || commentError || error) && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={
            errorMessage ?? commentError?.toString() ?? error?.toString()
          }
        />
      )}
    </div>
  );
};

export default ClientEventDetailedActionComponent;
